import React, { useState, useEffect } from 'react';
import { useForm } from '@formspree/react';
import '../components/DBOSHomepage.css';
import SiteHeader from '../components/SiteHeader';
import './SmartWeb.css';

const Technology = () => {
  const [state, handleSubmit] = useForm("mojkooov");
  const [navOpen, setNavOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [expandedLayers, setExpandedLayers] = useState({});

  const toggleLayer = (layerNum) => {
    setExpandedLayers(prev => ({
      ...prev,
      [layerNum]: !prev[layerNum]
    }));
  };
  useEffect(() => {
    const updateOpenStatus = () => {
      const now = new Date();
      const dublinTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Dublin"}));
      const day = dublinTime.getDay();
      const hours = dublinTime.getHours();
      const minutes = dublinTime.getMinutes();
      const currentTime = hours * 60 + minutes;

      let isOpen = false;
      if (day >= 1 && day <= 4) {
        isOpen = currentTime >= 540 && currentTime < 1080;
      } else if (day === 5) {
        isOpen = currentTime >= 540 && currentTime < 720;
      }
      
      const indicator = document.querySelector('.status-indicator');
      const hoursText = document.querySelector('.hours-text');
      
      const hoursContainer = document.querySelector('.opening-hours');
      
      if (indicator && hoursText && hoursContainer) {
        if (isOpen) {
          indicator.className = 'status-indicator open';
          hoursText.textContent = 'OPEN';
          hoursContainer.classList.add('status-open');
          hoursContainer.classList.remove('status-closed');
        } else {
          indicator.className = 'status-indicator closed';
          hoursText.textContent = 'CLOSED';
          hoursContainer.classList.remove('status-open');
          hoursContainer.classList.add('status-closed');
        }
      }
    };

    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  return (
    <div className="solution-page">
      <SiteHeader active="technology" />

      <main className="solution-content">
        <div className="container">
          <section className="hero-section">
            <h1 className="page-title">The AI Mainframe for Every SME</h1>
            <p className="page-subtitle">Enterprise-grade infrastructure powering the Digital Business Operating System</p>
            <p className="hero-intro">Built on Google Cloud Platform, with GitLab-based CI/CD and OpenTofu infrastructure-as-code, DBOS delivers a structured, layered architecture designed specifically for regulated SMEs.</p>
          </section>

          <section className="content-section">
            <h2>Core Principle: "No GID, Nothing Exists"</h2>
            <p>Every entity - customer, partner, location, document, or transaction - begins with a Global Identity (GID). This foundational layer makes every interaction traceable and consistent from day one. It handles consent, access rights, privacy, and metadata without making business decisions - globally consistent, deliberately free of regional logic.</p>
          </section>

          <section className="content-section">
            <h2>The Problem We Solve</h2>
            <p>Small businesses everywhere face the same challenge: they need their tools to actually work together, but end up cobbling together 10+ disconnected systems - each with its own login, its own data silo, its own integration headache. The result is wasted hours, scattered information, and constant small frictions that add up.</p>
            <p><strong>Traditional approaches fall short because:</strong></p>
            <ul>
              <li>Off-the-shelf systems are built for one kind of business, not yours</li>
              <li>SaaS tools don't talk to each other</li>
              <li>There's no single source of truth for what's actually happening in the business</li>
              <li>AI tools generate content without understanding your context</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Security Gates: Nothing Reaches DBOS Untested</h2>
            <p><strong>Gate 1 - File Security:</strong> Every file entering DBOS passes through a dedicated security gate before it reaches storage or AI processing, built on ClamAV malware scanning. Fully built and ready, rolling out across territories.</p>
            <p><strong>Gate 2 - Agent Communication Security:</strong> No communication from an AI agent reaches production without first passing through an isolation and verification gate. Live today, powering SmartMail and SmartWeb, with wider platform rollout underway.</p>
          </section>

          <section className="content-section">
            <h2>Our Solution: A Seven-Layer AI Mainframe Architecture</h2>
            <p>DBOS isn't software—it's an operating system. We've built a structured, AI-powered mainframe architecture specifically for regulated businesses, deployed on Google Cloud Platform and Alibaba Cloud infrastructure.</p>
          </section>

          <div className="layers-grid">
            {[0, 1, 2, 3, 4, 5, 6, 7].map(layerNum => {
                const layers = [
                  {
                    title: "Layer 0: Global Identity (GIDO)",
                    status: "Live",
                    purpose: "Universal identity for every organization, contact, and object across DBOS - the foundation everything else depends on.",
                    stack: ["Firebase Auth for authentication", "Firestore for the identity registry", "Cloud Functions for GID minting"],
                    aiRole: "None. This layer is deliberately AI-free to ensure deterministic identity management and compliance."
                  },
                  {
                    title: "Layer I: SmartData Intake Architecture",
                    status: "Live",
                    purpose: "Universal data ingestion with AI intelligence - automatic categorisation, metadata extraction, and natural-language search over every file.",
                    stack: ["Cloud Storage for signed-URL uploads", "Cloud Vision for image labelling and OCR", "Document AI for invoice and structured-data extraction", "Vertex AI embeddings for semantic search", "Pub/Sub for the async processing pipeline"],
                    aiRole: "Classification and extraction only. AI organises and labels - it never makes a business decision."
                  },
                  {
                    title: "Layer II: Angel UI / Portal",
                    status: "Live",
                    purpose: "The client-facing interface layer - home to SmartView, SmartShow, SmartShop, and SmartWeb.",
                    stack: ["React PWA for the client interface", "Firebase Hosting", "Cloud Functions for portal APIs"],
                    principles: "Zero typing where possible. AI explains, the human decides.",
                    aiRole: "Conversational and visual support. AI surfaces options and context - the human takes the action."
                  },
                  {
                    title: "Layer III: Regional Context Engine",
                    status: "Live (early version)",
                    purpose: "Per-territory regulatory and predictive context, so every solution behaves correctly for its jurisdiction. First live in an early version powering SmartFinance's jurisdiction-aware rules.",
                    stack: ["Firestore for territory-scoped context", "Cloud Functions for jurisdiction rule resolution"],
                    aiRole: "None yet at this stage. Deterministic geospatial and regulatory logic."
                  },
                  {
                    title: "Layer IV: Policy & Automation Engine",
                    status: "Planned",
                    purpose: "AI-driven recommendations and pattern recognition, built on top of Layer III's context.",
                    stack: ["Design and roadmap work only - depends on Layer III"],
                    aiRole: "Advisory only, once built. AI describes, the operator decides."
                  },
                  {
                    title: "Layer V: Financial, Compliance & Audit Spine",
                    status: "Deferred",
                    purpose: "A platform-wide immutable truth layer witnessing every financial transaction and data access.",
                    stack: ["SmartFinance already runs its own append-only ledger at solution level"],
                    aiRole: "RAG-based, once built. AI would answer from the verified ledger - never generating speculative content."
                  },
                  {
                    title: "Layer VI: Tax & Financial Structuring",
                    status: "Planned",
                    purpose: "Economic and IP structuring across jurisdictions, once scale calls for it.",
                    stack: ["Design work only - not yet started"],
                    aiRole: "None. Tax structuring requires human expertise and regulatory oversight."
                  },
                  {
                    title: "Layer VII: The Transcendent Layer",
                    status: "Vision-stage",
                    purpose: "What the platform is ultimately for, beyond the platform itself. Rooted in a Gr\u00fcnderv\u00e4ter ethos and a 'giving while living' philosophy, the long-term intent is to structure a charitable trust around DBOS's activities - funding central R&D and giving back to the local communities we serve. Full scope to follow in its own time.",
                    stack: ["Reserved - not yet defined"],
                    aiRole: "Not applicable at this stage."
                  }
                ];
              
              const layer = layers[layerNum];
              const isExpanded = expandedLayers[layerNum];
              
              return (
                <div key={layerNum} className="layer-tile">
                  <div className="layer-header" onClick={() => toggleLayer(layerNum)}>
                    <h3 className="layer-title">{layer.title}</h3>
                    <span className="layer-toggle">{isExpanded ? '−' : '+'}</span>
                  </div>
                  {isExpanded && (
                    <div className="layer-content">
                      <p><strong>Purpose:</strong> {layer.purpose}</p>
                        {layer.status && <p><strong>Status:</strong> {layer.status}</p>}
                      {layer.inputs && <p><strong>Inputs:</strong> {layer.inputs}</p>}
                      {layer.hardware && <p><strong>Hardware Support:</strong> {layer.hardware}</p>}
                      {layer.principles && <p><strong>Principles:</strong> {layer.principles}</p>}
                      {layer.output && <p><strong>Output:</strong> {layer.output}</p>}
                      {layer.examples && <p><strong>Examples:</strong> {layer.examples}</p>}
                      {layer.subsystems && <p><strong>Subsystems:</strong> {layer.subsystems}</p>}
                      {layer.cadence && <p><strong>Cadence:</strong> {layer.cadence}</p>}
                      <p><strong>Technical Stack:</strong></p>
                      <ul>
                        {layer.stack.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <p><strong>AI Role:</strong> {layer.aiRole}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>


          <section className="content-section">
            <h2>Our AI Philosophy: Two Modes, One Principle</h2>
            <p>We use AI in two distinct ways, depending on the use case and regulatory requirements:</p>
            
            <div className="ai-modes">
              <div className="mode-card">
                <h3>1. Suggesting AI (Layer IV)</h3>
                <p><strong>Use Case:</strong> Pattern recognition, forecasting, operational recommendations</p>
                <p><strong>Principle:</strong> <em>AI describes, the user decides.</em></p>
                <p><strong>Example:</strong> "Based on last year's data, we predict a 23% spike in demand next Tuesday. Recommend adding two slots."</p>
                <p><strong>Why:</strong> Suggestions accelerate decisions but never bypass human judgment.</p>
              </div>

              <div className="mode-card">
                <h3>2. RAG-Based AI (Layer V)</h3>
                <p><strong>Use Case:</strong> Compliance queries, audit responses, financial Q&A</p>
                <p><strong>Principle:</strong> <em>AI retrieves verified truth, never speculates.</em></p>
                <p><strong>Example:</strong> "What were our GDPR-related expenses in Q3?" → AI pulls directly from the append-only ledger, citing exact transactions.</p>
                <p><strong>Why:</strong> Regulated businesses can't afford hallucinations. RAG ensures every answer is traceable to source data.</p>
              </div>
            </div>
          </section>

          <section className="content-section">
            <h2>Why We Built It This Way</h2>
            <p><strong>Cloud Infrastructure:</strong> We build on Google Cloud Platform, with data held strictly within its own territory - Ireland, Germany, and the United States today.</p>
            <p><strong>Infrastructure as Code:</strong> Every environment is defined in OpenTofu, version-controlled in GitLab. Zero drift, instant rollback, auditable changes.</p>
            <p><strong>Security, Built In From Day One:</strong> DBOS is architected to align with ISO 27001 and SOC 2 principles, GDPR, and the EU AI Act from the ground up - built the way it needs to work, from day one.</p>
          </section>

          <section className="content-section">
            <h2>Why We're Better Than Alternatives</h2>
            <ul>
              <li><strong>One System, Any Sector:</strong> Whether you run a shop, a trade, or a compliance-heavy practice, DBOS gives you the same solid foundation - most tools force you to choose between simple and serious. We don't.</li>
              <li><strong>AI That Respects You:</strong> AI describes, you decide. Suggestions, never silent decisions made on your behalf.</li>
              <li><strong>No Vendor Lock-In:</strong> Your data exports to JSON, PDF, or direct API access. You own it.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Who We Are</h2>
            <p><strong>Founded:</strong> 2026 in Ireland. <strong>Operations:</strong> Ireland, Germany, and the United States.</p>
            <p><strong>Architecture:</strong> 4 architects, covering security and identity architecture, legacy-system migration, and cross-market delivery across Ireland and Germany.</p>
          </section>

          <section className="content-section">
            <h2>Trust & Track Record</h2>
            <p>We're a young company, but not a young team. Our founder brings 25 years of IT experience spanning SMB to global blue-chip enterprises - ITIL practice and high-transactional platforms, having worked with organisations including Dell (Germany), BMC Software (US), Synaxon (UK), and Lufthansa (Germany). Every layer of DBOS is built to the standard that experience demands: GDPR-first, EU AI Act aware, ISO 27001 and SOC 2 aligned by design.</p>
          </section>

          <section className="content-section">
            <h2>Positive Business Outcomes</h2>
            <p><strong>When you work with us, you get:</strong></p>
            <ul>
              <li><strong>Less Tool-Switching:</strong> One system, replacing the five to ten your business is probably running today.</li>
              <li><strong>Less Manual Re-Entry:</strong> Data flows through the system once, not copy-pasted between five tabs.</li>
              <li><strong>Less Hunting:</strong> Find the file or figure you know exists somewhere, in seconds.</li>
              <li><strong>Audit-Ready by Design:</strong> Built to hold up under scrutiny from day one, whether or not you're in a regulated sector.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>The DBOS Rules</h2>
            <p>These principles govern everything we build:</p>
            <ol>
              <li><strong>No GID, nothing exists.</strong> Identity first, always.</li>
              <li><strong>Layers 0 through III run today,</strong> Layer III in an early version. Layer IV is next. Layer V (the audit spine) and Layer VI (financial structuring) follow once there's more transaction history to build them around.</li>
              <li><strong>AI describes, the operator decides.</strong> Suggestions, not commands.</li>
              <li><strong>Truth is append-only wherever it's built.</strong> SmartFinance's ledger works this way today; the platform-wide version follows the same rule.</li>
              <li><strong>Compliance is how we build, from day one.</strong> Not a feature bolted on later.</li>
              <li><strong>Simplicity beats complexity.</strong> If a partner needs training, we failed.</li>
              <li><strong>Build for every SME, sector aside.</strong> If it holds up for a compliance-heavy business, it holds up for everyone.</li>
            </ol>
          </section>

          <section className="cta-section">
            <h2>Ready to Build on DBOS?</h2>
            <p className="cta-lead">Whether you're a regulated business looking for an operating system, a hyperscaler partner evaluating our architecture for AI startup credits, or a developer who wants to build the future of compliance tech - let's talk.</p>
            
            <form className="hero-form" onSubmit={handleSubmit} style={{marginTop: '32px'}}>
              <div className="form-row">
                <input 
                  type="text" 
                  name="name" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="Your Name" 
                  required 
                  className="form-input" 
                />
                <input 
                  type="email" 
                  name="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="your@email.com" 
                  required 
                  className="form-input" 
                />
                <button type="submit" className="btn-primary shimmering-btn" disabled={state.submitting}>
                  Let's Start a Sustainable Partnership
                  <span className="btn-arrow">→</span>
                </button>
              </div>
              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={termsAccepted} 
                    onChange={(e) => setTermsAccepted(e.target.checked)} 
                    required 
                  />
                  <span>I accept the <a href="/legal/terms">terms</a> & <a href="/legal/privacy">privacy policy</a></span>
                </label>
              </div>
            </form>
            
            {state.succeeded && (
              <p style={{color: "var(--dbos-gold)", textAlign: "center", marginTop: "20px", fontSize: "1.1rem"}}>
                ✓ Thank you! We'll contact you soon.
              </p>
            )}
          </section>
        </div>
      </main>

      <footer className="footer">
        <div className="container footer-compact">
          <div className="footer-row-1">
            <span className="footer-hours-inline"><span className="hours-label">Opening Hours (IST):</span> Mon-Thu 9:00-18:00 | Fri 9:00-12:00 | Sat-Sun Closed</span>
          </div>
          <div className="footer-row-2">
            <span className="copyright">© 2026 DBOS. Ireland (CRO Registered). All rights reserved.</span>
            <span className="footer-divider">|</span>
            <a href="/legal/terms">Terms</a>
            <a href="/legal/privacy">Privacy</a>
            <a href="/legal/cookies">Cookies</a>
            <a href="/legal/dpa">DPA</a>
            <span className="footer-divider">|</span>
            <a href="mailto:hello@dbos.world">hello@dbos.world</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Technology;
